import { useFormik } from "formik";
import React, { useCallback, useEffect } from "react";
import { useState } from "react";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Modal from "react-bootstrap/esm/Modal";
import { PartnerLocation } from "../../data/location";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { isEmail } from "../../lib/validation/string";
import {
  OfferingMessageType,
  usePartnerContactFormQuery,
  usePartnerLocationsQuery,
} from "../../offering/api";
import { PickerFormField } from "./picker_form_field";
import { TextFormField } from "./text_form_field";
import { useAnalytics } from "../../lib/analytics/analytics";
import { useQueryString } from "@/hooks";
import { useParams } from "react-router-dom";

export interface TalkWithUsPromptProps {
  partnerID: string;
  messageableType?: OfferingMessageType;
  messageableID: string;
  hubspotForm?: React.ReactNode;
  hubspotForms?: {
    [key: string]: React.ReactNode;
  };
}

export function TalkWithUsPrompt(props: TalkWithUsPromptProps) {
  const {
    partnerID,
    messageableID,
    hubspotForm,
    messageableType = OfferingMessageType.PartnerOffering,
    hubspotForms,
  } = props;
  const [show, setShow] = useState(false);
  const [data, setData] = useState<QuestionareData | null>(null);
  const { fbt } = useI18n();
  const { fromCatalog } = useQueryString<{ fromCatalog?: string }>();
  const pageParams = useParams<{ catalogSlug?: string }>();

  const hubspotFormByCatalog = fromCatalog || pageParams.catalogSlug;
  const hubspotFormToUse = hubspotForms?.[hubspotFormByCatalog || "default"]
    ? hubspotForms[hubspotFormByCatalog || "default"]
    : hubspotForm;

  return (
    <>
      <div className="d-flex align-items-center justify-content-end flex-wrap talk-with-us">
        <h6 className="mb-0 talk-with-us__need-help">{fbt("needHelp")}</h6>
        <Button
          variant="link"
          onClick={() => setShow(true)}
          className="text-decoration-none text-primary talk-with-us__link"
        >
          {fbt("Talk with us")}
        </Button>
      </div>
      <Modal show={show} fullscreen="sm-down" onHide={() => setShow(false)}>
        <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
        <Modal.Body className="pt-0">
          {hubspotFormToUse ? (
            hubspotFormToUse
          ) : data ? (
            <LetsTalkConfirmation
              data={data}
              onClose={() => {
                setShow(false);
                setData(null);
              }}
            />
          ) : (
            <LetsTalkForm
              partnerID={partnerID}
              messageableID={messageableID}
              onComplete={setData}
              messageableType={messageableType}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

interface ContactUsPromptProps {
  partnerID: string;
  messageableType?: OfferingMessageType;
  messageableID: string;
  hubspotForm?: React.ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function ContactUsPrompt(props: ContactUsPromptProps) {
  const {
    partnerID,
    messageableID,
    hubspotForm,
    messageableType = OfferingMessageType.PartnerOffering,
    open,
    setOpen,
  } = props;
  const [data, setData] = useState<QuestionareData | null>(null);

  return (
    <Modal show={open} fullscreen="sm-down" onHide={() => setOpen(false)}>
      <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
      <Modal.Body className="pt-0">
        {hubspotForm ? (
          hubspotForm
        ) : data ? (
          <LetsTalkConfirmation
            data={data}
            onClose={() => {
              setOpen(false);
              setData(null);
            }}
          />
        ) : (
          <LetsTalkForm
            partnerID={partnerID}
            messageableID={messageableID}
            onComplete={setData}
            messageableType={messageableType}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}

interface LetsTalkFormProps {
  partnerID: string;
  messageableID: string;
  messageableType: OfferingMessageType;
  onComplete: (data: QuestionareData) => void;
}

export function LetsTalkForm(props: LetsTalkFormProps) {
  const { messageableID, onComplete, messageableType, partnerID } = props;
  const { data: partnerContactForm } = usePartnerContactFormQuery(partnerID);
  const locations = usePartnerLocationsQuery(partnerID);
  const options = locations.map((l) => ({
    label: `${l.name} - ${l.streetAddress}`,
    value: l.id,
  }));
  const initLocation = locations.length === 1 ? locations[0] : null;
  const [selectedLocation, setSelectedLocation] =
    useState<PartnerLocation | null>(initLocation);
  const { fbt } = useI18n();
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.event(
      "form_start",
      { form_name: "lets_talk_form" },
      { onlyProviders: ["ga4"] }
    );
  }, [analytics]);

  const { values, errors, status, submitForm, setFieldValue } = useFormik({
    initialValues: {
      locationID: "",
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      message: "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validate: (v) => {
      const errors: { [field: string]: string } = {};

      if (!v.locationID) {
        errors.locationID = fbt("Please select a location");
      }
      if (!v.firstName) {
        errors.firstName = fbt("Please add your first name");
      }

      if (!v.lastName) {
        errors.lastName = fbt("Please add your last name");
      }

      if (!v.email) {
        errors.email = fbt("Please add your email");
      } else if (!isEmail(v.email)) {
        errors.email = fbt("Please enter a valid email");
      }

      if (!v.message) {
        errors.message = fbt("Message can't be blank");
      }

      return errors;
    },
    onSubmit: (v, { setStatus }) => {
      analytics.event(
        "form_submit",
        { form_name: "lets_talk_form" },
        { onlyProviders: ["ga4"] }
      );
      analytics.event(
        "Talk With Us Form Submit",
        {
          Email: v.message,
          Message: v.message,
        },
        { onlyProviders: ["mixpanel"] }
      );
      fetch(
        `/api/v2/offering_message?messageable_type=${messageableType}&messageable_id=${messageableID}&partner_id=${partnerID}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            first_name: v.firstName,
            last_name: v.lastName,
            email: v.email,
            phone_number: v.phoneNumber,
            message: v.message,
            location_id: v.locationID,
          }),
        }
      )
        .then(async (result) => {
          if (result.status >= 200 && result.status < 300) {
            onComplete(v);
            return;
          }

          setStatus("Message can't be blank");
        })
        .catch((e) => {
          setStatus("Error submitting form");
          console.error(e.message);
        });
    },
  });

  const onChangeLocation = useCallback(
    (locationID: string) => {
      const newSelectedLocation = locations.find((l) => l.id === locationID);
      setSelectedLocation(newSelectedLocation || null);
      setFieldValue("locationID", locationID);

      if (!newSelectedLocation) {
        return;
      }

      analytics.event(
        "Let's Talk Location Click",
        {
          Location: newSelectedLocation.name,
          "Location UUID": newSelectedLocation.id,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    },
    [analytics, locations, setFieldValue]
  );

  useEffect(() => {
    if (selectedLocation && selectedLocation?.id !== values.locationID) {
      // This is initial selection when open the pop up
      setFieldValue("locationID", selectedLocation.id);
      analytics.event(
        "Let's Talk Location Click",
        {
          Location: selectedLocation.name,
          "Location UUID": selectedLocation.id,
          Source: "WL",
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );
    }
  }, [analytics, selectedLocation, setFieldValue, values.locationID]);

  const onSubmitForm = useCallback(async () => {
    await submitForm();

    if (!selectedLocation) {
      return;
    }

    analytics.event(
      "Let's Talk Submit Click",
      {
        Location: selectedLocation.name,
        "Location UUID": selectedLocation.id,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  }, [analytics, submitForm, selectedLocation]);

  return (
    <>
      <h3>{partnerContactForm?.title || fbt("Let's talk")}</h3>
      <p>
        {partnerContactForm?.description ||
          fbt(
            "We would be happy to provide you with more information and answer your specific office needs."
          )}
      </p>
      <div className="row">
        <div className="col-12 mb-4">
          <PickerFormField
            name="location"
            label={fbt("Location")}
            options={options}
            value={values.locationID}
            onChange={onChangeLocation}
            placeholder={fbt("Select a location")}
            error={errors.locationID}
          />
        </div>
        <div className="col-12 mb-4">
          <TextFormField
            onChange={(firstName) => setFieldValue("firstName", firstName)}
            name="firstName"
            label={fbt("First Name")}
            value={values.firstName}
            error={errors.firstName}
          />
        </div>
        <div className="col-12 mb-4">
          <TextFormField
            onChange={(lastName) => setFieldValue("lastName", lastName)}
            name="Last Name"
            label={fbt("Last Name")}
            value={values.lastName}
            error={errors.lastName}
          />
        </div>
        <div className="col-12 mb-4">
          <TextFormField
            onChange={(email) => setFieldValue("email", email)}
            name="Email"
            label={fbt("Email")}
            value={values.email}
            error={errors.email}
          />
        </div>
        <div className="col-12 mb-4">
          <TextFormField
            onChange={(phoneNumber) =>
              setFieldValue("phoneNumber", phoneNumber)
            }
            name="phoneNumber"
            label={fbt("phoneNumberOptional")}
            value={values.phoneNumber}
            error={errors.phoneNumber}
          />
        </div>
        <div className="col-12 mb-4">
          <TextFormField
            rows={5}
            onChange={(message) => setFieldValue("message", message)}
            name="message"
            label={fbt("How can we help?")}
            value={values.message}
            error={errors.message}
          />
        </div>
        {selectedLocation && (
          <div className="col-12 mb-4">
            <p className="text-muted text-end">
              {partnerContactForm?.partnerPhoneBlurb || fbt("Or call us at:")}{" "}
              <a href={`tel:${selectedLocation.contactPhone}`}>
                {selectedLocation.phoneNumberMask ||
                  getPhoneLast4Character(selectedLocation.contactPhone)}
              </a>
            </p>
          </div>
        )}
        {status && (
          <div className="col-12 mb-4">
            <Form.Text className="text-danger">{status}</Form.Text>
          </div>
        )}
        <div className="col-12 mb-4 offset-md-6 col-md-6">
          <Button variant="primary" onClick={onSubmitForm} className="w-100">
            {fbt("Submit")}
          </Button>
        </div>
      </div>
    </>
  );
}

function getPhoneLast4Character(partnerPhoneNumber: string) {
  const phoneNumber =
    partnerPhoneNumber.length <= 4
      ? partnerPhoneNumber
      : partnerPhoneNumber.slice(partnerPhoneNumber.length - 4);
  return `*${phoneNumber}`;
}

interface QuestionareData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  message: string;
}

interface LetsTalkConfirmationProps {
  data: QuestionareData;
  onClose: () => void;
}

function LetsTalkConfirmation(props: LetsTalkConfirmationProps) {
  const { data, onClose } = props;
  const { fbt } = useI18n();
  return (
    <>
      <h3>{fbt("Let's talk")}</h3>
      <p>
        {fbt(
          "Thank you " +
            fbt.param("firstName", data.firstName) +
            ", we will get back to you as soon as we can and reply to your email at " +
            fbt.param("email", data.email) +
            "."
        )}
      </p>
      <div className="row">
        <div className="col-12 mb-4 offset-md-6 col-md-6">
          <Button variant="primary" onClick={onClose} className="w-100">
            {fbt("Close")}
          </Button>
        </div>
      </div>
    </>
  );
}
